import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Html5Qrcode } from "html5-qrcode";
import { useNavigate } from "react-router-dom";
import GuruLayout from "../../components/layout/GuruLayout";
import { usePopup } from "../../components/modal/Popup"; // ← dari folder modal

const API = "https://backend.smpn1kamal.sch.id/api";

const STATUS_BADGE = {
  hadir: "bg-green-500 text-white",
  izin:  "bg-yellow-400 text-white",
  sakit: "bg-blue-400 text-white",
  alfa:  "bg-gray-200 text-gray-600",
};

const STATUS_ROW = {
  hadir: "bg-green-50 border-green-200",
  izin:  "bg-yellow-50 border-yellow-200",
  sakit: "bg-blue-50 border-blue-200",
  alfa:  "bg-white border-gray-100",
};

export default function PresensiScanner() {
  const navigate      = useNavigate();
  const scannerRef    = useRef(null);
  const sudahHadirRef = useRef(new Set());
  const siswaListRef  = useRef([]);

  const { success: popSuccess, error: popError, PopupPortal } = usePopup();

  const [jadwal,      setJadwal]     = useState(null);
  const [siswaList,   _setSiswaList] = useState([]);
  const [closing,     setClosing]    = useState(false);
  const [scanReady,   setScanReady]  = useState(false);
  const [facingMode,  setFacingMode] = useState("environment");

  const setSiswaList = useCallback((updater) => {
    _setSiswaList((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      siswaListRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    const init = async () => {
      try {
        const res = await axios.get(`${API}/jadwal/aktif`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.data.active) {
          toast.error("Tidak ada jadwal aktif saat ini");
          navigate("/guru/presensi");
          return;
        }

        const jadwalAktif = res.data.jadwal;
        setJadwal(jadwalAktif);

        const siswaRes = await axios.get(
          `${API}/datasiswa/kelas/${jadwalAktif.id_kelas}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        let statusMap = {};
        try {
          const presensiRes = await axios.get(
            `${API}/presensi/by-kelas?id_kelas=${jadwalAktif.id_kelas}&id_mapel=${jadwalAktif.id_mapel}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          for (const p of presensiRes.data.data ?? []) {
            statusMap[p.id] = p.status;
          }
        } catch { /* lanjut dengan alfa */ }

        const merged = siswaRes.data.map((s) => ({
          ...s,
          status: statusMap[s.id] ?? "alfa",
        }));
        setSiswaList(merged);

        for (const [id, status] of Object.entries(statusMap)) {
          if (status === "hadir") sudahHadirRef.current.add(Number(id));
        }

        setTimeout(() => {
          startScanner(token, "environment");
          setScanReady(true);
        }, 300);

      } catch (err) {
        console.error("Init error:", err);
        toast.error("Gagal memulai scanner. Pastikan kamera tersedia.");
        navigate("/guru/presensi");
      }
    };

    init();
    return () => { stopScanner(); };
  }, [navigate, setSiswaList]);

  const startScanner = (token, facing = "environment") => {
    const readerEl = document.getElementById("reader");
    if (!readerEl) return;
    if (scannerRef.current) stopScanner();

    const html5QrCode = new Html5Qrcode("reader");
    scannerRef.current = html5QrCode;

    const qrboxFn = (w, h) => {
      const size = Math.floor(Math.min(w, h) * 0.65);
      return { width: size, height: size };
    };

    const tryStart = (mode, fallback) => {
      html5QrCode
        .start(
          { facingMode: mode },
          { fps: 20, qrbox: qrboxFn, aspectRatio: 1.0 },
          onScanSuccess(token)
        )
        .then(() => {
          const video = readerEl.querySelector("video");
          if (video) {
            video.style.objectFit = "cover";
            video.style.width = "100%";
            video.style.height = "100%";
          }
        })
        .catch(() => {
          if (fallback) tryStart(fallback, null);
          else toast.error("Kamera tidak bisa diakses.");
        });
    };

    tryStart(facing, facing === "environment" ? "user" : "environment");
  };

  const switchCamera = async () => {
    const token = sessionStorage.getItem("token");
    const nextFacing = facingMode === "environment" ? "user" : "environment";
    await stopScanner();
    setFacingMode(nextFacing);
    setTimeout(() => startScanner(token, nextFacing), 300);
  };

  const onScanSuccess = (token) => async (text) => {
    let data;
    try { data = JSON.parse(text); } catch { return; }
    if (!data?.id_siswa) return;

    const idSiswa = Number(data.id_siswa);
    if (sudahHadirRef.current.has(idSiswa)) return;

    const siswa = siswaListRef.current.find((s) => s.id === idSiswa);
    const statusSekarang = siswa?.status ?? "alfa";

    sudahHadirRef.current.add(idSiswa);
    setSiswaList((prev) =>
      prev.map((s) => s.id === idSiswa ? { ...s, status: "hadir" } : s)
    );

    try {
      await axios.post(
        `${API}/presensi/scan`,
        { id_siswa: idSiswa },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      popSuccess(siswa?.nama_lengkap ?? "Siswa", "Hadir tercatat");
    } catch (err) {
      console.error("Scan POST error:", err);
      popError("Gagal menyimpan scan", err.response?.data?.message);
      sudahHadirRef.current.delete(idSiswa);
      setSiswaList((prev) =>
        prev.map((s) => s.id === idSiswa ? { ...s, status: statusSekarang } : s)
      );
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        if (state === 2) await scannerRef.current.stop();
        await scannerRef.current.clear();
      } catch { /* abaikan */ }
      scannerRef.current = null;
    }
  };

  const tutup = async () => {
    setClosing(true);
    const token = sessionStorage.getItem("token");
    try {
      await stopScanner();

      await axios.post(
        `${API}/presensi/manual`,
        { data: siswaListRef.current.map((s) => ({ id_siswa: s.id, status: s.status })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (jadwal) {
        await axios.post(
          `${API}/presensi/wa`,
          { id_kelas: jadwal.id_kelas },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        popSuccess("Presensi dikirim", "Notifikasi WA berhasil terkirim");
      }

      setTimeout(() => {
        navigate("/guru/presensi");
      }, 1200);

    } catch (err) {
      console.error("Tutup error:", err);
      popError("Gagal mengirim presensi");
      setClosing(false);
      startScanner(sessionStorage.getItem("token"), facingMode);
    }
  };

  const jumlahHadir     = siswaList.filter((s) => s.status === "hadir").length;
  const jumlahIzinSakit = siswaList.filter((s) => s.status === "izin" || s.status === "sakit").length;
  const total           = siswaList.length;

  return (
    <GuruLayout>
      {PopupPortal}

      <style>{`
        #reader video {
          object-fit: cover !important;
          width: 100% !important;
          height: 100% !important;
        }
        #reader {
          background-color: black !important;
        }
        #reader__dashboard {
          display: none !important;
        }
      `}</style>

      <div className="p-4 md:p-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/guru/presensi")}
            className="flex items-center gap-1 text-sm text-gray-400 hover:text-gray-700 transition-colors shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali
          </button>

          {jadwal && (
            <>
              <span className="text-gray-200 select-none">|</span>
              <div className="min-w-0">
                <h1 className="text-sm md:text-base font-semibold text-gray-800 truncate">
                  {jadwal.nama_kelas}
                  <span className="text-gray-300 mx-1.5">·</span>
                  {jadwal.nama_mapel}
                </h1>
                <p className="text-xs text-gray-400">Presensi Scan QR &mdash; {total} siswa</p>
              </div>
            </>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-5">
          <div className="w-full md:w-[380px] shrink-0 flex flex-col gap-3">
            <div className="relative">
              <div
                id="reader"
                className={`relative rounded-2xl overflow-hidden border transition-all duration-300 ${
                  scanReady
                    ? "border-blue-400 shadow-lg shadow-blue-100"
                    : "border-gray-200"
                }`}
                style={{ width: "100%", height: "300px" }}
              />

              <button
                onClick={switchCamera}
                className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5 transition-all backdrop-blur-md border border-white/10"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            <div className="min-h-[1.25rem]">
              {jumlahIzinSakit > 0 && (
                <p className="text-[11px] font-medium text-blue-500 text-center italic">
                  {jumlahIzinSakit} siswa izin/sakit dapat di-scan menjadi hadir
                </p>
              )}
            </div>

            <button
              onClick={tutup}
              disabled={closing}
              className="w-full bg-red-500 hover:bg-red-600 active:scale-[0.98] text-white py-3 rounded-full font-bold text-sm transition-all shadow-md shadow-red-100 disabled:opacity-50"
            >
              {closing ? "Memproses..." : "Tutup & Kirim WA"}
            </button>
          </div>

          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-3 px-1">
              <p className="text-sm font-bold text-gray-700">Daftar Siswa</p>
              <span className="text-xs text-gray-500">
                <span className="font-bold text-green-600">{jumlahHadir}</span>
                <span className="mx-1">/</span>
                {total} hadir
              </span>
            </div>

            <div
              className="space-y-2 overflow-y-auto pr-1"
              style={{ maxHeight: "380px" }}
            >
              {siswaList.map((s, idx) => (
                <div
                  key={s.id}
                  className={`flex items-center justify-between border rounded-xl px-4 py-3 text-sm transition-all ${
                    STATUS_ROW[s.status] ?? STATUS_ROW.alfa
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-xs text-gray-400 font-mono w-5 shrink-0">
                      {(s.no_absen ?? idx + 1).toString().padStart(2, '0')}
                    </span>
                    <span className="font-semibold text-gray-700 truncate">
                      {s.nama_lengkap}
                    </span>
                  </div>
                  <span className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider shrink-0 ml-3 ${
                    STATUS_BADGE[s.status] ?? STATUS_BADGE.alfa
                  }`}>
                    {s.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </GuruLayout>
  );
}